import { useState } from 'react';
import './css/help.css';
import { motion, AnimatePresence } from 'framer-motion';

// --- DATA DEFINITIONS ---
const categories = [
    { id: 'technical', name: 'Technical Support', icon: 'fa-solid fa-wrench' },
    { id: 'payment', name: 'Payment & Billing', icon: 'fa-solid fa-credit-card' },
    { id: 'account', name: 'Account Issues', icon: 'fa-solid fa-user-shield' },
    { id: 'exams', name: 'Exam Related', icon: 'fa-solid fa-laptop-file' },
    { id: 'general', name: 'General Queries', icon: 'fa-solid fa-circle-question' }
];

const faqs = {
    technical: [
        {
            question: "How to reset my password?",
            answer: "Go to the Login page and click on 'Forgot Password'. You'll receive a secure link on your registered email to reset it."
        },
        {
            question: "The mock test is not loading properly, what should I do?",
            answer: "Please clear your browser cache, ensure you are using the latest version of Chrome or Firefox, and check your internet connection stability."
        }
    ],
    payment: [
        {
            question: "What payment methods do you accept?",
            answer: "We securely accept UPI, major Credit/Debit Cards, Net Banking, and popular wallets like Paytm and PhonePe."
        },
        {
            question: "My payment failed but money was deducted. What now?",
            answer: "Don't worry. Failed transactions are usually refunded automatically by your bank within 5-7 working days. If you still face issues, contact our support with the transaction ID."
        }
    ],
    account: [
        {
            question: "Can I share my account with my friends?",
            answer: "No. Account sharing is strictly prohibited as per our Terms and Conditions. Our system tracks concurrent logins and may permanently ban accounts found violating this rule."
        }
    ],
    exams: [
        {
            question: "Can I pause a live mock test?",
            answer: "Yes, you can pause most of the practice tests. However, strictly timed live mock tests mimicking real exams cannot be paused once started."
        }
    ],
    general: [
        {
            question: "Is the platform available in Hindi?",
            answer: "Yes, most of our test series and mock exams support a bilingual format (English and Hindi) to ensure maximum accessibility."
        }
    ]
};

const Help = () => {
    //const name = process.env.REACT_APP_APP_NAME || "ExamWorld";
    const support_mail = process.env.REACT_APP_APP_SUPPOE_MAIL || "support@examworld.in";

    const [activeCategory, setActiveCategory] = useState('technical');
    const [expandedFaq, setExpandedFaq] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    return (
        <div className="helpPageWrapper">
            
            {/* Header Section */}
            <header className="helpHeaderContainer">
                <h2 className="helpTitle">Help & Support Center</h2>
                <p className="helpSubtitle">
                    Find quick answers to your questions or get in touch with our team.
                </p>
                
                {/* Search Bar */}
                <div className="searchWrapper">
                    <i className="fa-solid fa-magnifying-glass searchIcon"></i>
                    <input 
                        type="text" 
                        placeholder="Search for answers..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="searchBtn">Search</button>
                </div>
            </header>

            <main className="helpMainContent">
                <div className="helpGridContainer">
                    
                    {/* Left: Sidebar Categories */}
                    <aside className="helpSidebar">
                        <h3>Categories</h3>
                        <div className="categoryList">
                            {categories.map((cat) => (
                                <button 
                                    key={cat.id}
                                    className={`categoryTab ${activeCategory === cat.id ? 'activeCat' : ''}`}
                                    onClick={() => {
                                        setActiveCategory(cat.id);
                                        setExpandedFaq(null);
                                    }}
                                >
                                    <i className={`${cat.icon} catIcon`}></i>
                                    <span className="catName">{cat.name}</span>
                                    <i className="fa-solid fa-chevron-right arrowIcon"></i>
                                </button>
                            ))}
                        </div>
                    </aside>

                    {/* Right: Dynamic FAQ Content */}
                    <section className="faqSection">
                        <div className="faqHeader">
                            <h3>{categories.find(c => c.id === activeCategory)?.name}</h3>
                            <p>Showing common questions and solutions.</p>
                        </div>
                        
                        <div className="faqAccordion">
                            {faqs[activeCategory] && faqs[activeCategory].length > 0 ? (
                                faqs[activeCategory].map((faq, index) => (
                                    <div key={index} className={`faqCard ${expandedFaq === index ? 'faqOpen' : ''}`}>
                                        <div className="faqTrigger" onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}>
                                            <h4>{faq.question}</h4>
                                            <i className={`fa-solid fa-plus toggleIcon ${expandedFaq === index ? 'rotateIcon' : ''}`}></i>
                                        </div>
                                        <AnimatePresence>
                                            {expandedFaq === index && (
                                                <motion.div 
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="faqBody"
                                                >
                                                    <p>{faq.answer}</p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))
                            ) : (
                                <div className="noDataBox">
                                    <i className="fa-solid fa-inbox textMuted"></i>
                                    <p>No FAQs found for this category yet.</p>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </main>

            {/* Contact Footer Section */}
            <footer className="helpContactSection modernCard">
                <h3>Still Stuck?</h3>
                <p>If you couldn't find the answer you were looking for, our support team is ready to assist you.</p>
                
                <div className="contactGrid">
                    <div className="contactCard">
                        <div className="contactIconBox"><i className="fa-solid fa-envelope"></i></div>
                        <h4>Email Us</h4>
                        <p>Get a response within 24 hours.</p>
                        <button 
                            className="contactActionBtn primaryOutline"
                            onClick={() => window.location.href = `mailto:${support_mail}`}
                        >
                            Send Email
                        </button>
                    </div>
                    <div className="contactCard">
                        <div className="contactIconBox"><i className="fa-solid fa-headset"></i></div>
                        <h4>Live Support</h4>
                        <p>Chat with our technical team.</p>
                        <button className="contactActionBtn primaryOutline">Start Chat</button>
                    </div>
                </div>
            </footer>
            
        </div>
    );
};

export default Help;